import { useState, useEffect, useCallback, FormEvent, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import './AuthVerifyPage.css';

export function AuthVerifyPage() {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const { verifyLink, verifyOTP, login, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Get token from URL if magic link was clicked
  const token = searchParams.get('token');
  const email = sessionStorage.getItem('pendingAuthEmail');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleMagicLink = useCallback(async (linkToken: string) => {
    setIsVerifying(true);
    setError(null);
    try {
      await verifyLink(linkToken);
      sessionStorage.removeItem('pendingAuthEmail');
      navigate('/dashboard', { replace: true });
    } catch {
      setError('Invalid or expired link. Please request a new one or enter the code manually.');
    } finally {
      setIsVerifying(false);
    }
  }, [verifyLink, navigate]);

  // Auto-verify magic link
  useEffect(() => {
    if (token) {
      handleMagicLink(token);
    }
  }, [token, handleMagicLink]);

  const handleOTPChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (newOtp.every(digit => digit !== '')) {
      handleOTPSubmit(undefined, newOtp.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newOtp = pasted.split('');
      setOtp(newOtp);
      inputRefs.current[5]?.focus();
      handleOTPSubmit(undefined, pasted);
    }
  };

  const handleOTPSubmit = async (e?: FormEvent, code?: string) => {
    e?.preventDefault();
    const otpCode = code || otp.join('');

    if (!email) {
      setError('Please start from the login page');
      return;
    }

    if (otpCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      await verifyOTP(email, otpCode);
      sessionStorage.removeItem('pendingAuthEmail');
      navigate('/dashboard', { replace: true });
    } catch {
      setError('Invalid code. Please check and try again.');
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      navigate('/login');
      return;
    }

    setIsResending(true);
    setError(null);
    setResendSuccess(false);

    try {
      await login(email);
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch {
      setError('Failed to resend code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  // Show loading state while verifying magic link
  if (token && isVerifying) {
    return (
      <div className="verify-page">
        <div className="corner corner-tl" />
        <div className="corner corner-tr" />
        <div className="corner corner-bl" />
        <div className="corner corner-br" />

        <div className="verify-container">
          <div className="verifying-state">
            <LoadingSpinner size="lg" />
            <p className="verifying-text">Verifying your link...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="verify-page">
      {/* Decorative corners */}
      <div className="corner corner-tl" />
      <div className="corner corner-tr" />
      <div className="corner corner-bl" />
      <div className="corner corner-br" />

      <div className="verify-container">
        {/* Brand */}
        <div className="brand">
          <Link to="/" className="logo-link">
            <h1 className="logo">
              Page<span>Craft</span>
            </h1>
            <p className="studio">Studio</p>
          </Link>
        </div>

        <div className="verify-card">
          <h2 className="verify-title">Enter Verification Code</h2>

          {email ? (
            <p className="verify-subtitle">
              Enter the 6-digit code sent to <strong>{email}</strong>
            </p>
          ) : (
            <p className="verify-subtitle">
              Enter the 6-digit code from your email
            </p>
          )}

          <form className="verify-form" onSubmit={handleOTPSubmit}>
            <div className="otp-inputs" onPaste={handlePaste}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => { inputRefs.current[index] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOTPChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="otp-input"
                  disabled={isVerifying}
                  autoFocus={index === 0}
                  aria-label={`Digit ${index + 1}`}
                />
              ))}
            </div>

            {error && (
              <div className="form-message error" role="alert">
                {error}
              </div>
            )}

            {resendSuccess && (
              <div className="form-message success" role="status">
                A new code has been sent to your email.
              </div>
            )}

            <button
              type="submit"
              className="submit-btn"
              disabled={isVerifying || otp.some(d => !d)}
            >
              {isVerifying ? (
                <>
                  <LoadingSpinner size="sm" className="btn-spinner" />
                  Verifying...
                </>
              ) : (
                'Verify Code'
              )}
            </button>
          </form>

          <div className="verify-actions">
            <button
              type="button"
              className="resend-btn"
              onClick={handleResend}
              disabled={isResending}
            >
              {isResending ? 'Sending...' : "Didn't receive a code? Resend"}
            </button>
          </div>
        </div>

        <footer className="verify-footer">
          <Link to="/login" className="back-link">
            &larr; Back to Login
          </Link>
        </footer>
      </div>
    </div>
  );
}
